import React from 'react'
import { cssClass } from '../../../shared/utils/styles'
import { LoaderSpinner } from '../../ui/LoaderSpinner.component'
import {
  inputFieldPlaceholderActive,
  inputFieldPlaceholder,
  inputFieldWithPlaceHolderActive,
} from './styles/input.css'
import {
  inputContainer,
  labelInput,
  inputContainerField,
  inputField,
  inputFieldError,
  loaderContainer,
  textArea,
  errorMessage,
} from './styles'

interface Props {
  label?: string
  placeholder?: string
  value?: string | number
  name?: string
  error?: string
  disabled?: boolean
  required?: boolean
  className?: string[]
  containerClassName?: string[]
  loading?: boolean
  minHeight?: number
  showError?: boolean
  usePlaceholder?: boolean
  autoHeight?: boolean
  padding?: string
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void
}

export const TextArea: React.FC<Props> = ({
  label,
  placeholder,
  value,
  onChange,
  name,
  error,
  disabled = false,
  required = false,
  className = [],
  containerClassName = [],
  onFocus,
  onBlur,
  minHeight = 130,
  loading = false,
  showError = false,
  usePlaceholder = false,
  autoHeight = false,
  padding = 'p-20',
}) => {
  const [isFocused, setIsFocused] = React.useState(false)
  const [height, setHeight] = React.useState<string | number>('auto')
  const hasValue = value !== '' && value !== undefined
  const textAreaRef = React.useRef<HTMLTextAreaElement>(null)

  const placeholderStyle =
    hasValue || isFocused ? inputFieldPlaceholderActive : inputFieldPlaceholder
  const inputFieldWithPlaceHolder =
    hasValue && placeholder ? inputFieldWithPlaceHolderActive : ''

  const handleOnfocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(true)
    onFocus && onFocus(e)
  }

  const handleOnBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(false)
    onBlur && onBlur(e)
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLTextAreaElement> & {
      key?: string
      shiftKey?: boolean
    }
  ) => {
    const { currentTarget, key, shiftKey } = e
    const currentValue = currentTarget.value
    const isDeleting = currentValue.length < String(value)?.length

    if (key === 'Enter' && !shiftKey) {
      e.preventDefault()
      const updatedValue = currentValue + '\n'

      if (autoHeight) {
        setHeight('auto')
        setHeight(currentTarget.scrollHeight)
      }

      const formattedValue = updatedValue.replace(/\n/g, '<br>')

      onChange &&
        onChange({ ...e, target: { ...e.target, value: formattedValue } })
    } else {
      if (autoHeight && (isDeleting || key === 'Backspace')) {
        setHeight('auto')
        setHeight(currentTarget.scrollHeight)
      } else {
        setHeight(currentTarget.scrollHeight)
      }

      onChange && onChange(e)
    }
  }

  return (
    <div className={cssClass([inputContainer, ...containerClassName])}>
      <label className={labelInput} htmlFor={name}>
        {label}
      </label>
      {placeholder && placeholder !== undefined && !usePlaceholder ? (
        <span className={placeholderStyle}>{placeholder}</span>
      ) : null}

      <div className={inputContainerField}>
        <textarea
          name={name}
          value={value}
          onChange={handleChange}
          disabled={disabled || loading}
          required={required}
          className={cssClass([
            inputField,
            inputFieldWithPlaceHolder,
            textArea,
            error || showError ? inputFieldError : '',
            padding,
            ...className,
          ])}
          style={{ minHeight: minHeight, height: height }}
          onFocus={handleOnfocus}
          onBlur={handleOnBlur}
          placeholder={usePlaceholder && placeholder ? placeholder : undefined}
          ref={textAreaRef}
        />
        {loading && (
          <div className={loaderContainer}>
            <LoaderSpinner size={15} background="rgba(255, 255, 255, 0.4)" />
          </div>
        )}
      </div>
      {error && <span className={errorMessage}>{error}</span>}
    </div>
  )
}
